import React, { useEffect, useCallback, useRef, forwardRef } from "react";
import { FlatList, Image, ScrollView, Text, View } from "react-native";
import { DEVICE_SIZES, useDeviceSize } from "rn-responsive-styles";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { TouchableOpacity } from "react-native";
import { AppImages } from "../../utility/AppImages";
import { useLiveStream } from "../../context/LiveStreamContext";
import { STREAM_STATUS } from "../../utility/LiveStreamSocket/LiveStreamConfig";
import Avatar from "../choose-avatar-components/Avatar";
import { LinearGradient } from "expo-linear-gradient";

const ObservePlayer = ({
  styles,
  chatOpen,
  setChatOpen,
  selectedPlayer,
  isTeam,
  setIsTeam,
  playerData,
  setSelectedPlayer,
  giftList,
  setPlayerData,
  onAvatarLayout, // New prop to send avatar positions to parent
}) => {
  const { audienceData, totalGiftedTokens, eventStartTime } = useLiveStream();
  const deviceSize = useDeviceSize();

  // Refs for avatar positions
  const player1AvatarRef = useRef<View>(null);
  const player2AvatarRef = useRef<View>(null);
  const bothAvatarRef = useRef<View>(null);

  // Function to measure and send avatar positions to parent
  const measureAvatarPositions = useCallback(() => {
    const positions: any = {};

    const checkAllPositionsMeasured = (pos: any) => {
      if (pos.player1 && pos.player2 && pos.both && onAvatarLayout) {
        onAvatarLayout(pos);
      }
    };

    if (player1AvatarRef.current) {
      player1AvatarRef.current.measure((x, y, width, height, pageX, pageY) => {
        positions.player1 = { x: pageX + width / 2, y: pageY + height / 2 };
        checkAllPositionsMeasured(positions);
      });
    }

    if (player2AvatarRef.current) {
      player2AvatarRef.current.measure((x, y, width, height, pageX, pageY) => {
        positions.player2 = { x: pageX + width / 2, y: pageY + height / 2 };
        checkAllPositionsMeasured(positions);
      });
    }

    if (bothAvatarRef.current) {
      bothAvatarRef.current.measure((x, y, width, height, pageX, pageY) => {
        positions.both = { x: pageX + width / 2, y: pageY + height / 2 };
        checkAllPositionsMeasured(positions);
      });
    }
  }, [onAvatarLayout]);

  const currentDateTime = new Date();
  const eventDateTime = eventStartTime ? new Date(eventStartTime) : null;
  const isWithinValidTime =
    eventDateTime &&
    !isNaN(eventDateTime.getTime()) &&
    currentDateTime.getTime() >= eventDateTime.getTime();

  const isOrganiserStreamCompleted =
    audienceData?.eventDetails?.streamStatus?.organiser ===
    STREAM_STATUS.COMPLETED;

  const getUserPlatform = (userType) => {
    const platforms =
      audienceData?.eventDetails?.streamingPlatforms?.[userType] || [];
    if (platforms.includes("Twitch")) return "twitch";
    if (platforms.includes("YouTube")) return "youtube";
    return null;
  };

  useEffect(() => {
    if (audienceData?.eventDetails?.status === "completed") {
      setSelectedPlayer("both");
    }
  }, [audienceData?.eventDetails?.status, setSelectedPlayer]);

  const getStreamId = (userType) => {
    if (!audienceData?.streamDetails?.[0]) return null;
    const {
      organizerYoutubeStream,
      challengerYoutubeStream,
      organizerTwitchStream,
      challengerTwitchStream,
    } = audienceData.streamDetails[0];

    const userPlatform = getUserPlatform(userType);

    if (userPlatform === "twitch" && userType === "organiser") {
      return organizerTwitchStream?.liveStreamURL;
    }
    if (userPlatform === "twitch" && userType === "challenger") {
      return challengerTwitchStream?.liveStreamURL;
    }
    if (userPlatform === "youtube" && userType === "organiser") {
      return organizerYoutubeStream?.liveStreamId;
    }
    if (userPlatform === "youtube" && userType === "challenger") {
      return challengerYoutubeStream?.liveStreamId;
    }
    return null;
  };

  const organiserStreamId = getStreamId("organiser");

  // Check if organiser or challenger is streaming live
  const isOrganiserStreamLive =
    audienceData?.eventDetails?.streamStatus?.organiser === "live";
  const isChallengerStreamLive =
    audienceData?.eventDetails?.streamStatus?.challenger === "live";
  const isEventLive = audienceData?.eventDetails?.status === "live";

  // Streaming is available if event is live OR any participant is streaming,
  // AND organiser stream is not completed, AND we're within valid time
  const isAnyStreamLive = isEventLive || isOrganiserStreamLive || isChallengerStreamLive;

  const isStreamingAvailable =
    isAnyStreamLive &&
    !isOrganiserStreamCompleted &&
    isWithinValidTime;

  useEffect(() => {
    if (audienceData?.participantDetails) {
      const transformedData = audienceData.participantDetails.map(
        (participant, index) => ({
          id: index + 1,
          userID: participant.userID,
          image: participant.profilePicture || "",
          name: participant.userID || "Unknown",
          type: `Player ${index + 1}`,
          watch: participant.userID === selectedPlayer ? "watching" : "watch",
        })
      );

      const combinedPlayerData = {
        id: transformedData.length + 1,
        isCombined: true,
      };

      setPlayerData([...transformedData, combinedPlayerData]);

      if (!selectedPlayer && transformedData.length > 0) {
        setSelectedPlayer(transformedData[0].userID);
      }
    }
  }, [audienceData, selectedPlayer]);

  const renderItem = useCallback(
    ({ item }) => {
      if (item.isCombined) {
        const player1 = playerData[0] || {
          name: "Player 1",
          image: "",
        };
        const player2 = playerData[1] || {
          name: "Player 2",
          image: "",
        };

        return (
          <View>
            <TouchableOpacity
              onPress={() => setSelectedPlayer("both")}
              style={{
                borderColor: AppColors.grayColor_505050,
                backgroundColor:
                  selectedPlayer === "both" ? "rgba(220, 38, 38, 0.20)" : "",
                borderRadius: 8,
                borderWidth: 1,
                flexDirection: "row",
                columnGap: 10,
                margin: 3,
                paddingHorizontal: 5,
                paddingVertical: 8,
              }}
            >
              <View
                style={{
                  borderRadius: 100,
                  borderColor: AppColors.appViolentColor_6D08C3,
                  borderWidth: 1,
                }}
              >
                <Avatar src={player1.image} variant="liveSmallPlayer" />
              </View>
              <View
                ref={bothAvatarRef}
                style={{ flex: 1, alignSelf: "center" }}
                onLayout={() => setTimeout(measureAvatarPositions, 100)}
              >
                <View
                  style={{
                    backgroundColor: AppColors.appViolentColor_6D08C3,
                    alignSelf: "center",
                    paddingHorizontal: 8,
                    paddingVertical: 5,
                    borderRadius: 5,
                    marginTop: 0,
                  }}
                >
                  <Text
                    style={[
                      deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                        ? AppFontStyle.REGULAR_ANYBODY_14
                        : AppFontStyle.REGULAR_ANYBODY_10,
                    ]}
                  >
                    {selectedPlayer === "both" ? "watching" : "watch"}
                  </Text>
                </View>
              </View>
              <View
                style={{
                  borderRadius: 100,
                  borderColor: AppColors.appViolentColor_6D08C3,
                  borderWidth: 1,
                }}
              >
                <Avatar src={player2.image} variant="liveSmallPlayer" />
              </View>
            </TouchableOpacity>
            <View
              style={{
                flexDirection: "row",
                justifyContent: "space-between",
                padding: 10,
              }}
            >
              <View>
                <Text
                  style={[
                    deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                      ? AppFontStyle.REGULAR_ANYBODY_14
                      : AppFontStyle.REGULAR_ANYBODY_12,
                  ]}
                >
                  {player1.name}
                </Text>
              </View>
              <View>
                <Text
                  style={[
                    deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                      ? AppFontStyle.REGULAR_ANYBODY_14
                      : AppFontStyle.REGULAR_ANYBODY_12,
                  ]}
                >
                  {player2.name}
                </Text>
              </View>
            </View>
          </View>
        );
      }

      return (
        <TouchableOpacity
          onPress={() => {
            if (isStreamingAvailable) {
              setSelectedPlayer(item.userID);
            } else {
              console.warn(
                "Cannot select individual players until both streams are available."
              );
            }
          }}
          style={{
            borderColor: AppColors.grayColor_505050,
            backgroundColor:
              item.userID === selectedPlayer ? "rgba(220, 38, 38, 0.20)" : "",
            borderRadius: 8,
            borderWidth: 1,
            flexDirection: "row",
            columnGap: 10,
            margin: 3,
            paddingHorizontal: 5,
            paddingVertical: 8,
          }}
        >
          <View
            ref={
              item.userID === audienceData?.participantDetails?.[0]?.userID
                ? player1AvatarRef
                : item.userID === audienceData?.participantDetails?.[1]?.userID
                  ? player2AvatarRef
                  : null
            }
            style={{
              borderRadius: 100,
              borderColor: AppColors.appViolentColor_6D08C3,
              borderWidth: 1,
            }}
            onLayout={() => setTimeout(measureAvatarPositions, 100)}
          >
            <Avatar src={item.image} variant="liveSmallPlayer" />
          </View>
          <View style={{ flex: 1, alignSelf: "center" }}>
            <Text
              style={[
                deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                  ? AppFontStyle.REGULAR_ANYBODY_14
                  : AppFontStyle.REGULAR_ANYBODY_12,
              ]}
            >
              {item.name}
            </Text>
            {/* <Text
            style={[
              deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                ? AppFontStyle.REGULAR_ANYBODY_10
                : AppFontStyle.REGULAR_ANYBODY_8,
              { marginTop: 5 },
            ]}
          >
            {item.type}
          </Text> */}
          </View>
          <View
            style={{
              backgroundColor: AppColors.appViolentColor_6D08C3,
              alignSelf: "center",
              paddingHorizontal: 8,
              paddingVertical: 5,
              borderRadius: 5,
            }}
          >
            <Text
              style={[
                deviceSize === DEVICE_SIZES.EXTRA_LARGE_DEVICE
                  ? AppFontStyle.REGULAR_ANYBODY_14
                  : AppFontStyle.REGULAR_ANYBODY_10,
              ]}
            >
              {item.watch}
            </Text>
          </View>
        </TouchableOpacity>
      );
    },
    [
      playerData,
      selectedPlayer,
      isStreamingAvailable,
      deviceSize,
      setSelectedPlayer,
    ]
  );

  return (
    <View
      style={[
        styles.observePlayerView,
        {
          width:
            deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM
              ? "100%"
              : deviceSize === DEVICE_SIZES.MD
                ? "32%"
                : deviceSize === DEVICE_SIZES.LG
                  ? "28%"
                  : undefined,
          marginTop:
            deviceSize === DEVICE_SIZES.XL ||
              deviceSize === DEVICE_SIZES.LG ||
              deviceSize === DEVICE_SIZES.MD
              ? 12
              : 0,
          marginBottom:
            deviceSize === DEVICE_SIZES.XL ||
              deviceSize === DEVICE_SIZES.LG ||
              deviceSize === DEVICE_SIZES.MD
              ? 12
              : 20,
        },
      ]}
    >
      {/* Conditional styling for web vs mobile */}
      {deviceSize === DEVICE_SIZES.XL ||
        deviceSize === DEVICE_SIZES.LG ||
        deviceSize === DEVICE_SIZES.MD ? (
        <LinearGradient
          colors={["rgba(31, 41, 55, 0.50)", "rgba(17, 24, 39, 0.50)"]}
          start={{ x: 0, y: 0 }}
          end={{ x: 0.8, y: 1 }}
          style={{
            borderRadius: 8,
            borderWidth: 1,
            borderColor: "#374151",
            padding: 12,
            flex: 1,
            flexDirection: "column",
          }}
        >
          {/* Header section */}
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
            }}
          >
            <Text style={[AppFontStyle.BOLD_ANYBODY_18, { flex: 1 }]}>
              Observe Player
            </Text>
          </View>

          {/* Divider */}
          {chatOpen && (
            <View
              style={{
                height: 1,
                backgroundColor: AppColors.grayColor_282828,
                marginVertical: 5,
              }}
            />
          )}

          {/* Content section - Scrollable Players List */}
          {chatOpen && (
            <>
              <ScrollView
                style={{ flex: 1 }}
                showsVerticalScrollIndicator={false}
              >
                <FlatList
                  data={playerData}
                  renderItem={renderItem}
                  keyExtractor={(item) => item.id.toString()}
                  showsVerticalScrollIndicator={false}
                  scrollEnabled={false}
                />
              </ScrollView>

              {/* Bottom section - Fixed at bottom */}
              {giftList && giftList.length > 0 && (
                <View style={{ marginTop: 10, marginBottom: 10 }}>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { marginBottom: 8 },
                    ]}
                  >
                    {audienceData.eventDetails.allOrNothing
                      ? "All Or Nothing Arena:"
                      : selectedPlayer === "both"
                        ? "Total Gifts:"
                        : selectedPlayer === playerData[0]?.name
                          ? "Organizer Gifts:"
                          : "Challenger Gifts:"}
                  </Text>

                  <ScrollView
                    contentContainerStyle={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      gap: 10,
                      paddingVertical: 5,
                    }}
                    style={{
                      maxHeight: 120,
                      borderRadius: 6,
                      borderWidth: 1,
                      borderColor: "#374151",
                      backgroundColor: "rgba(0, 0, 0, 0.2)",
                      padding: 8,
                    }}
                    showsVerticalScrollIndicator={true}
                    showsHorizontalScrollIndicator={false}
                  >
                    {giftList.map((item: any, idx: number) => (
                      <View
                        style={{
                          alignItems: "center",
                          justifyContent: "flex-start",
                          width: 50,
                        }}
                        key={`${item.giftId}-${item.receiverId || "all"
                          }-${idx}`}
                      >
                        <Image
                          source={item.image ?? AppImages.icWhiteDollarIcon}
                          style={styles.sideGiftImage}
                          resizeMode="contain"
                        />
                        <View style={{ flexDirection: "row", width: 50 }}>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_8,
                              {
                                marginTop: 5,
                                width: "100%",
                                textAlign: "center",
                              },
                            ]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {item.giftId}
                          </Text>

                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_8,
                              {
                                marginTop: 5,
                              },
                            ]}
                          >
                            ({item.quantity || 1})
                          </Text>
                        </View>
                      </View>
                    ))}
                  </ScrollView>
                  <Text
                    style={[AppFontStyle.REGULAR_ANYBODY_10, { marginTop: 8 }]}
                  >
                    {audienceData.eventDetails.allOrNothing
                      ? "Winner takes all gifts"
                      : "Gifts displayed based on selection"}
                  </Text>
                </View>
              )}

              <View
                style={{
                  height: 1,
                  backgroundColor: AppColors.gray_343434,
                  marginTop: 10,
                  marginBottom: 10,
                }}
              />

              {/* Total Tokens - Fixed at bottom */}
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                  alignItems: "center",
                  paddingVertical: 8,
                  paddingHorizontal: 6,
                }}
              >
                <View>
                  <Image
                    source={AppImages.purpleComputer}
                    style={{ height: 60, width: 80 }}
                    resizeMode="contain"
                  />
                </View>
                <View style={{ justifyContent: "center", alignItems: "flex-end" }}>
                  <Text style={[AppFontStyle.REGULAR_ANYBODY_16, {}]}>
                    {totalGiftedTokens ?? 0} Tokens
                  </Text>
                </View>
              </View>
            </>
          )}
        </LinearGradient>
      ) : (
        <>
          <View
            style={{
              flexDirection: "row",
              justifyContent: "space-between",
              backgroundColor: !chatOpen
                ? AppColors.gray_1E1E1E
                : "transparent",
              padding: chatOpen ? 0 : 12,
              borderRadius: !chatOpen ? 10 : 0,
              borderColor: !chatOpen
                ? AppColors.grayColor_333333
                : "transparent",
              borderWidth: !chatOpen ? 1 : 0,
            }}
          >
            <Text style={[AppFontStyle.BOLD_ANYBODY_18, { flex: 1 }]}>
              Observe Player
            </Text>
          </View>

          {/* Mobile content */}
          {chatOpen && (
            <View
              style={{
                height: 1,
                backgroundColor: AppColors.grayColor_282828,
                marginVertical: 5,
              }}
            />
          )}
          {chatOpen && (
            <>
              <View
                style={{
                  flexDirection: "row",
                  backgroundColor: AppColors.grayColor_505050,
                  borderRadius: 10,
                  justifyContent: "space-between",
                  marginVertical: 18,
                }}
              >
                <TouchableOpacity
                  onPress={() => {
                    setIsTeam("Team1");
                  }}
                  style={[
                    styles.btnTeam,
                    {
                      backgroundColor:
                        isTeam === "Team1"
                          ? AppColors.gray_1E1E1E
                          : AppColors.grayColor_505050,
                      borderRadius: 10,
                      flex: 1,
                    },
                  ]}
                >
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      {
                        textAlign: "center",
                      },
                    ]}
                  >
                    Challengers
                  </Text>
                </TouchableOpacity>
              </View>
              <FlatList
                data={playerData}
                renderItem={renderItem}
                keyExtractor={(item) => item.id.toString()}
              />
              {giftList && giftList.length > 0 && (
                <View>
                  <Text
                    style={[
                      AppFontStyle.REGULAR_ANYBODY_14,
                      { marginBottom: 5 },
                    ]}
                  >
                    {audienceData.eventDetails.allOrNothing
                      ? "All Or Nothing Arena:"
                      : selectedPlayer === "both"
                        ? "Total Gifts:"
                        : selectedPlayer === playerData[0]?.name
                          ? "Organizer Gifts:"
                          : "Challenger Gifts:"}
                  </Text>

                  <ScrollView
                    contentContainerStyle={{
                      flexDirection: "row",
                      flexWrap: "wrap",
                      justifyContent: "flex-start",
                      gap: 10,
                    }}
                    style={styles.scrollView}
                    showsVerticalScrollIndicator={false}
                    showsHorizontalScrollIndicator={false}
                  >
                    {giftList.map((item, idx) => (
                      <View
                        style={{
                          alignItems: "center",
                          justifyContent: "flex-start",
                          width: 50,
                        }}
                        key={`${item.giftId}-${item.receiverId || "all"
                          }-${idx}`}
                      >
                        <Image
                          source={item.image ?? AppImages.icWhiteDollarIcon}
                          style={styles.sideGiftImage}
                          resizeMode="contain"
                        />
                        <View style={{ flexDirection: "row", width: 50 }}>
                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_8,
                              {
                                marginTop: 5,
                                width: "100%",
                                textAlign: "center",
                              },
                            ]}
                            numberOfLines={1}
                            ellipsizeMode="tail"
                          >
                            {item.giftId}
                          </Text>

                          <Text
                            style={[
                              AppFontStyle.REGULAR_ANYBODY_8,
                              {
                                marginTop: 5,
                              },
                            ]}
                          >
                            ({item.quantity || 1})
                          </Text>
                        </View>
                      </View>
                    ))}
                  </ScrollView>
                  <Text
                    style={[AppFontStyle.REGULAR_ANYBODY_10, { marginTop: 5 }]}
                  >
                    {audienceData.eventDetails.allOrNothing
                      ? "Winner takes all gifts"
                      : "Gifts displayed based on selection"}
                  </Text>
                </View>
              )}

              <View
                style={{
                  height: 1,
                  backgroundColor: AppColors.gray_343434,
                  marginVertical: 15,
                }}
              />
              <View
                style={{
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                <View>
                  <Image
                    source={AppImages.purpleComputer}
                    style={{ height: 75, width: 105, marginTop: 3 }}
                    resizeMode="contain"
                  />
                </View>
                <View style={{ justifyContent: "center" }}>
                  <Text style={[AppFontStyle.REGULAR_ANYBODY_16, {}]}>
                    {totalGiftedTokens ?? 0} Tokens
                  </Text>
                </View>
              </View>
            </>
          )}
        </>
      )}
    </View>
  );
};

export default React.memo(ObservePlayer);
